import React, { useState, useEffect } from 'react';
import { databases, ID, ensureAuth } from '../services/appwriteClient';
import { Query } from 'appwrite';
import { Student } from '../types';
import { useAuth } from '../context/AuthContext';
import { Download, Edit2, Calendar, Check } from 'lucide-react';
import { getStudents } from '../services/studentService';
import { createAttendanceAlert } from '../services/attendanceService';

type AttendanceStatus = 'present' | 'absent' | 'late';

interface AttendanceRecord {
  studentId: string;
  status: 'present' | 'absent';
  date: string;
}

interface StudentHistory {
  date: string;
  status: 'present' | 'absent' | 'late';
  remarks?: string;
}

interface StudentHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
}

const DATABASE_ID = '682acd26001f69683cd8';
const STUDENTS_COLLECTION_ID = '682acd33000e34dd93c0';
const ATTENDANCE_COLLECTION_ID = '683078a400301e04d0e8'; // New attendance collection ID

/**
 * Attendance Collection Structure in Appwrite:
 * 
 * Collection ID: 682ad0280010003caa67
 * 
 * Attributes:
 * - student_id (string, required)
 *   - Description: ID of the student
 *   - Size: 36 characters
 * 
 * - status (string, required)
 *   - Description: Attendance status (present/absent)
 *   - Size: 10 characters
 *   - Default: "present"
 * 
 * - date (string, required)
 *   - Description: Date of attendance
 *   - Format: YYYY-MM-DD
 * 
 * - created_at (string, required)
 *   - Description: Timestamp of record creation
 *   - Format: ISO string
 * 
 * - updated_at (string, required)
 *   - Description: Timestamp of last update
 *   - Format: ISO string
 * 
 * Permissions:
 * - Create: role:all
 * - Read: role:all
 * - Update: role:all
 * - Delete: role:all
 */

const StudentHistoryModal: React.FC<StudentHistoryModalProps> = ({ isOpen, onClose, studentId, studentName }) => {
  const [history, setHistory] = useState<StudentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchStudentHistory();
    }
  }, [isOpen, studentId]);

  const fetchStudentHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const response = await databases.listDocuments(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        [
          Query.equal('student_id', studentId),
          Query.greaterThanEqual('date', thirtyDaysAgo.toISOString().split('T')[0]),
          Query.orderDesc('date')
        ]
      );

      setHistory(response.documents.map((doc: any) => ({
        date: doc.date,
        status: doc.Status,
        remarks: doc.remarks
      })));
    } catch (err) {
      console.error('Error fetching student history:', err);
      setError('Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{studentName}'s Attendance History</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center">{error}</div>
          ) : (
            <div className="space-y-2">
              {history.map((record) => (
                <div
                  key={record.date}
                  className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-gray-400" />
                    <span className="text-gray-600">{new Date(record.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-2 py-1 rounded text-sm ${
                      record.status === 'present'
                        ? 'bg-green-100 text-green-800'
                        : record.status === 'absent'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {record.status}
                    </span>
                    {record.remarks && (
                      <span className="text-sm text-gray-600 max-w-[200px] truncate">
                        {record.remarks}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DebouncedRemarkInput: React.FC<{
  value: string;
  onDebouncedChange: (value: string) => void;
}> = ({ value, onDebouncedChange }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onDebouncedChange(localValue);
      }
    }, 1500);
    setDebounceTimer(timer);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [localValue]);

  return (
    <input
      type="text"
      value={localValue}
      onChange={e => setLocalValue(e.target.value)}
      className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      placeholder="Add remarks..."
    />
  );
};

const AttendancePage: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [attendance, setAttendance] = useState<Record<string, { status: 'present' | 'absent' | 'late', remarks: string }>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<Date | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<{ id: string; name: string } | null>(null);

  // Get available groups based on selected level
  const getAvailableGroups = () => {
    switch (selectedLevel) {
      case '1':
        return [
          { value: 'larva1', label: 'Larva 1' },
          { value: 'larva2', label: 'Larva 2' },
          { value: 'larva3', label: 'Larva 3' }
        ];
      case '2':
        return [
          { value: 'pupa1', label: 'Pupa 1' },
          { value: 'pupa2', label: 'Pupa 2' },
          { value: 'pupa3', label: 'Pupa 3' }
        ];
      case '3':
        return [
          { value: 'caterpillar1', label: 'Caterpillar 1' },
          { value: 'caterpillar2', label: 'Caterpillar 2' },
          { value: 'caterpillar3', label: 'Caterpillar 3' }
        ];
      case '4':
        return [
          { value: 'butterfly1', label: 'Butterfly 1' },
          { value: 'butterfly2', label: 'Butterfly 2' },
          { value: 'butterfly3', label: 'Butterfly 3' }
        ];
      default:
        return [];
    }
  };

  // Reset group selection when level changes
  useEffect(() => {
    setSelectedGroup('all');
  }, [selectedLevel]);

  // Fetch students and their existing attendance for the selected date
  const fetchStudentsAndAttendance = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all students
      const allStudents = await getStudents();
      console.log('Attendance Page - Total students fetched:', allStudents.length);

      // Filter students based on selected level and group
      const filteredStudents = allStudents.filter(student => {
        const levelMatch = selectedLevel === 'all' || student.level.toString() === selectedLevel;
        const groupMatch = selectedGroup === 'all' || student.group === selectedGroup;
        return levelMatch && groupMatch;
      });

      console.log('Attendance Page - Filtered students:', filteredStudents.length);

      // Fetch existing attendance for the selected date
      const response = await databases.listDocuments(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        [
          Query.equal('date', selectedDate),
          Query.limit(500) // Increased limit to handle larger classes
        ]
      );

      // Create a map of existing attendance records
      const existingAttendanceMap: Record<string, { status: 'present' | 'absent' | 'late', remarks: string }> = {};
      response.documents.forEach((doc: any) => {
        existingAttendanceMap[doc.student_id] = {
          status: doc.Status,
          remarks: doc.remarks || ''
        };
      });

      console.log('Attendance Page - Existing records found:', response.documents.length);

      // Initialize attendance state for filtered students
      const initialAttendance = filteredStudents.reduce((acc, student) => {
        acc[student.id] = existingAttendanceMap[student.id] || {
          status: 'present',
          remarks: ''
        };
        return acc;
      }, {} as Record<string, { status: 'present' | 'absent' | 'late'; remarks: string }>);

      setStudents(filteredStudents);
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Error fetching students and attendance:', error);
      setError('Failed to fetch students and attendance data');
    } finally {
      setLoading(false);
    }
  };

  // Single useEffect to load all data when filters change
  useEffect(() => {
    fetchStudentsAndAttendance();
  }, [selectedDate, selectedLevel, selectedGroup]);

  const handleAttendanceChange = (studentId: string, status: 'present' | 'absent' | 'late') => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], status }
    }));
  };

  const handleMarkAllPresent = () => {
    const updatedAttendance = { ...attendance };
    students.forEach(student => {
      updatedAttendance[student.id] = {
        status: 'present',
        remarks: attendance[student.id]?.remarks || ''
      };
    });
    setAttendance(updatedAttendance);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);

      // Validate that students exist
      if (students.length === 0) {
        setError('No students to mark attendance for');
        return;
      }

      // Validate that attendance has been marked
      if (Object.keys(attendance).length === 0) {
        setError('Please mark attendance for students');
        return;
      }

      await ensureAuth();

      // Get all existing attendance records for the selected date for ALL students (not just filtered)
      const existingRecords = await databases.listDocuments(
        DATABASE_ID,
        ATTENDANCE_COLLECTION_ID,
        [
          Query.equal('date', selectedDate),
          Query.limit(500)
        ]
      );

      // Create a map of existing records by student_id
      const existingRecordsMap = new Map(
        existingRecords.documents.map((doc: any) => [doc.student_id, doc.$id])
      );

      // Update or create attendance records for the current filtered students
      const attendancePromises = Object.entries(attendance).map(async ([studentId, record]) => {
        const existingRecordId = existingRecordsMap.get(studentId);

        if (existingRecordId) {
          // Update existing record
          return databases.updateDocument(
            DATABASE_ID,
            ATTENDANCE_COLLECTION_ID,
            existingRecordId,
            {
              Status: record.status,
              remarks: record.remarks || '',
              Updated_at: new Date().toISOString()
            }
          );
        } else {
          // Create new record
          return databases.createDocument(
            DATABASE_ID,
            ATTENDANCE_COLLECTION_ID,
            ID.unique(),
            {
              student_id: studentId,
              date: selectedDate,
              Status: record.status,
              remarks: record.remarks || '',
              Created_at: new Date().toISOString(),
              Updated_at: new Date().toISOString()
            }
          );
        }
      });

      await Promise.all(attendancePromises);

      // Create attendance alerts for absent students
      const absentStudents = Object.entries(attendance)
        .filter(([, record]) => record.status === 'absent')
        .map(([studentId]) => studentId);

      if (absentStudents.length > 0) {
        const alertPromises = absentStudents.map(async (studentId) => {
          try {
            await createAttendanceAlert(studentId, selectedDate);
          } catch (alertError) {
            // Alert errors are logged but don't fail the submission
            console.error(`Error creating attendance alert for student ${studentId}:`, alertError);
          }
        });

        await Promise.all(alertPromises);
      }

      setSuccess(true);
      setLastSubmissionTime(new Date());
      localStorage.setItem('lastAttendanceSubmission', new Date().toISOString());

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

      console.log(`Successfully submitted attendance for ${Object.keys(attendance).length} students on ${selectedDate}`);
    } catch (error) {
      console.error('Error submitting attendance:', error);
      setError('Failed to submit attendance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Add function to fetch last submission time
  const fetchLastSubmissionTime = () => {
    if (user?.role === 'teacher') {
      const lastSubmission = localStorage.getItem('lastAttendanceSubmission');
      if (lastSubmission) {
        setLastSubmissionTime(new Date(lastSubmission));
      }
    }
  };

  // Update useEffect to fetch last submission time when component mounts
  useEffect(() => {
    fetchLastSubmissionTime();
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Attendance Management</h1>

      {/* Level Selector */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Select Level</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setSelectedLevel('all')}
            className={`px-4 py-2 rounded-md ${selectedLevel === 'all' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            All Levels
          </button>
          <button
            onClick={() => setSelectedLevel('1')}
            className={`px-4 py-2 rounded-md ${selectedLevel === '1' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Level 1
          </button>
          <button
            onClick={() => setSelectedLevel('2')}
            className={`px-4 py-2 rounded-md ${selectedLevel === '2' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Level 2
          </button>
          <button
            onClick={() => setSelectedLevel('3')}
            className={`px-4 py-2 rounded-md ${selectedLevel === '3' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Level 3
          </button>
          <button
            onClick={() => setSelectedLevel('4')}
            className={`px-4 py-2 rounded-md ${selectedLevel === '4' ? 'bg-pink-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Level 4
          </button>
        </div>
      </div>

      {/* Group Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Group</label>
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          disabled={selectedLevel === 'all'}
        >
          <option value="all">All Groups</option>
          <option value="larva1">Larva 1</option>
          <option value="larva2">Larva 2</option>
          <option value="larva3">Larva 3</option>
          <option value="pupa1">Pupa 1</option>
          <option value="pupa2">Pupa 2</option>
          <option value="pupa3">Pupa 3</option>
          <option value="caterpillar1">Caterpillar 1</option>
          <option value="caterpillar2">Caterpillar 2</option>
          <option value="caterpillar3">Caterpillar 3</option>
          <option value="butterfly">Butterfly</option>
        </select>
      </div>

      {/* Mark All Present Button */}
      <div className="mb-6">
        <button
          onClick={handleMarkAllPresent}
          className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Mark All Present
        </button>
      </div>

      {/* Date Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Date</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Attendance Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Remarks
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {students.map((student) => (
              <tr key={student.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <button
                      onClick={() => setSelectedStudent({ id: student.id, name: student.name })}
                      className="text-sm font-medium text-pink-600 hover:text-pink-800 transition-colors"
                    >
                      {student.name}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAttendanceChange(student.id, 'present')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        attendance[student.id]?.status === 'present'
                          ? 'bg-green-600 text-white'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      Present
                    </button>
                    <button
                      onClick={() => handleAttendanceChange(student.id, 'late')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        attendance[student.id]?.status === 'late'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      }`}
                    >
                      Late
                    </button>
                    <button
                      onClick={() => handleAttendanceChange(student.id, 'absent')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        attendance[student.id]?.status === 'absent'
                          ? 'bg-red-600 text-white'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      Absent
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="text"
                    value={attendance[student.id]?.remarks || ''}
                    onChange={(e) => {
                      setAttendance(prev => ({
                        ...prev,
                        [student.id]: {
                          ...prev[student.id],
                          remarks: e.target.value
                        }
                      }));
                    }}
                    className="border border-gray-300 rounded-md px-3 py-1 text-sm w-full bg-white"
                    placeholder="Add remarks..."
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : 'Submit Attendance'}
        </button>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mt-4 bg-green-100 p-4 rounded-md text-green-700">
          Attendance submitted successfully!
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-100 p-4 rounded-md text-red-700">
          {error}
        </div>
      )}

      {/* Last Submission Time */}
      {lastSubmissionTime && (
        <div className="mt-4 text-sm text-gray-600">
          Last submitted: {lastSubmissionTime.toLocaleString()}
        </div>
      )}

      {/* Student History Modal */}
      {selectedStudent && (
        <StudentHistoryModal
          isOpen={true}
          studentId={selectedStudent.id}
          studentName={selectedStudent.name}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
};

export default AttendancePage;